import { useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useMemo, useRef } from 'react'
import { titleFromQuestion } from '../../shared/mentions.js'
import { ChatHeader } from '../components/ChatHeader.jsx'
import { Composer } from '../components/composer/Composer.jsx'
import { newConversationId } from '../lib/ids.js'
import { upsertConversationInLists } from '../lib/queries/conversations.js'
import { useSystems } from '../lib/queries/systems.js'
import { queueQuestion } from '../state/pending.js'
import p from './pages.module.css'

export function NewQuestionPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: systems = [] } = useSystems()
  const composerRef = useRef(null)

  const bySlug = useMemo(() => new Map(systems.map((s) => [s.slug, s])), [systems])
  const suggestions = useMemo(
    () =>
      systems
        .filter((s) => s.status !== 'none')
        .flatMap((s) => s.suggestions ?? [])
        .filter((sg) => sg.systems.every((slug) => bySlug.get(slug) && bySlug.get(slug).status !== 'none'))
        .slice(0, 3),
    [systems, bySlug],
  )

  const onSubmit = ({ text, attachments = [] }) => {
    const id = newConversationId()
    const now = Date.now()
    // Optimistic: the conversation is in the sidebar before the request starts.
    upsertConversationInLists(queryClient, {
      id,
      title: titleFromQuestion(text || attachments.map((a) => a.name).join(', ')),
      titleSource: 'question',
      systems: [],
      createdAt: now,
      updatedAt: now,
      lastMessageAt: now,
    })
    queueQuestion(id, text, attachments)
    navigate({ to: '/c/$conversationId', params: { conversationId: id } })
    return true
  }

  return (
    <div className={p.chat}>
      <ChatHeader title="New question" />
      <div className={p.center}>
        <div className={p.centerColumn}>
          <div className={p.hero}>
            <h2 className={p.heroTitle}>What do you need to know?</h2>
            <p className={p.heroText}>
              Ask about any system in the library, or type @ to tag the systems your question is about. Answers come
              from each system's skill files — endpoints, field maps and how the systems connect.
            </p>
          </div>

          {suggestions.length ? (
            <div className={p.section}>
              <span className="section-label">Try asking</span>
              <div className={p.grid3}>
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion.text}
                    type="button"
                    className={p.suggestCard}
                    onClick={() => composerRef.current?.setQuestion({ text: suggestion.text })}
                  >
                    {suggestion.text}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
      <Composer
        ref={composerRef}
        placeholder="Ask about an endpoint, a field or how two systems connect — type @ to tag a system"
        systems={systems}
        autoFocus
        onSubmit={onSubmit}
      />
    </div>
  )
}
